import { useState, useEffect, useCallback } from 'react';

interface IUseDropdownPosition {
  triggerRef: React.RefObject<any>;
  dropdownRef: React.RefObject<any>;
}

interface IUseDropdownPositionReturn {
  position: {
    top: string;
    left: string;
    bottom: string;
    right: string;
  };
}

const useDropdownPosition = ({ triggerRef, dropdownRef }: IUseDropdownPosition): IUseDropdownPositionReturn => {
  const [position, setPosition] = useState({
    top: '-1000%',
    left: '-1000%',
    bottom: 'auto',
    right: 'auto',
  });

  const handlePositioning = useCallback(() => {
    if (triggerRef.current && dropdownRef.current) {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const dropdownWidth = dropdownRef.current.offsetWidth;
      const dropdownHeight = dropdownRef.current.offsetHeight;

      const spaceLeft = triggerRect.left;
      const spaceRight = viewportWidth - triggerRect.right;
      const spaceAbove = triggerRect.top;
      const spaceBelow = viewportHeight - triggerRect.bottom;

      const newPosition = {
        top: '-1000%',
        left: '-1000%',
        bottom: 'auto',
        right: 'auto',
      };

      // Vertical positioning
      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        newPosition.top = 'auto';
        newPosition.bottom = '100%';
      } else {
        newPosition.top = '100%';
        newPosition.bottom = 'auto';
      }

      // Horizontal positioning
      if (spaceRight < dropdownWidth && spaceLeft > spaceRight) {
        newPosition.left = 'auto';
        newPosition.right = '0';
      } else {
        newPosition.left = '0';
        newPosition.right = 'auto';
      }

      setPosition(newPosition);
    }
  }, [dropdownRef, triggerRef]);

  useEffect(() => {
    handlePositioning();

    // Re-position dropdown on window resize
    window.addEventListener('resize', handlePositioning);

    // Clean up event listener
    return () => window.removeEventListener('resize', handlePositioning);
  }, [triggerRef, dropdownRef, handlePositioning]);

  return { position };
};

export default useDropdownPosition;
